import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

/** Single in-flight / completed load for the page (@googlemaps/js-api-loader is one key globally). */
let loadPromise: Promise<void> | null = null

let configuredApiKey: string | null = null

let coreLib: google.maps.CoreLibrary | null = null
let mapsLib: google.maps.MapsLibrary | null = null
let markerLib: google.maps.MarkerLibrary | null = null

/** Core primitives (`event`, `LatLngBounds`, `SymbolPath`, …) — after {@link loadGoogleMapsPlacesScript}. */
export function getGoogleMapsCoreLibrary(): google.maps.CoreLibrary {
  if (!coreLib) {
    throw new Error('Google Maps: call loadGoogleMapsPlacesScript() before using the API')
  }
  return coreLib
}

/** Map, Data, InfoWindow, … — after {@link loadGoogleMapsPlacesScript}. */
export function getGoogleMapsMapsLibrary(): google.maps.MapsLibrary {
  if (!mapsLib) {
    throw new Error('Google Maps: call loadGoogleMapsPlacesScript() before using the API')
  }
  return mapsLib
}

/** AdvancedMarkerElement, PinElement, … — after {@link loadGoogleMapsPlacesScript}. */
export function getGoogleMapsMarkerLibrary(): google.maps.MarkerLibrary {
  if (!markerLib) {
    throw new Error('Google Maps: call loadGoogleMapsPlacesScript() before using the API')
  }
  return markerLib
}

/**
 * Loads Maps JavaScript API via `@googlemaps/js-api-loader` using `importLibrary` (Google’s pattern),
 * then `places` and `marker`. Concurrent calls share one load; the first API key wins for the page.
 */
export async function loadGoogleMapsPlacesScript(apiKey: string): Promise<void> {
  const key = apiKey.trim()
  if (!key) {
    throw new Error('Google Maps API key is empty')
  }

  if (typeof window === 'undefined') {
    throw new Error('Google Maps can only load in a browser')
  }

  if (configuredApiKey !== null && configuredApiKey !== key) {
    console.warn(
      '[loadGoogleMapsPlacesScript] @googlemaps/js-api-loader uses one API key per page; ignoring a different key.'
    )
  }

  loadPromise ??= loadOnce(key)
  await loadPromise
}

async function loadOnce(key: string): Promise<void> {
  configuredApiKey = key
  setOptions({ key, v: 'weekly' })

  mapsLib = await importLibrary('maps')
  coreLib = await importLibrary('core')
  await importLibrary('places')
  markerLib = await importLibrary('marker')

  if (typeof mapsLib.Map !== 'function') {
    throw new Error('Google Maps API Map constructor is not available')
  }
}
